<template>
  <div
    @click="e => this.$emit('click', e)"
    class="app-card"
    :class="{
      inverted,
      compact,
      'minimal-vertical-padding': minimalVerticalPadding,
      'top-shadow': topShadow,
      'full-height': fullHeight,
      'no-border-radius': noBorderRadius,
      'no-vertical-padding': noVerticalPadding,
      'no-padding': noPadding,
      transparent,
      'has-background-image': backgroundImage !== undefined,
    }"
    :style="{
      color: textColor,
      backgroundColor,
      'background-image': currentBackgroundImage
        ? `url('${currentBackgroundImage}')`
        : undefined,
      'border-top-left-radius': borderTopRadius,
      'border-top-right-radius': borderTopRadius,
      'border-bottom-left-radius': borderBottomRadius,
      'border-bottom-right-radius': borderBottomRadius,
    }"
  >
    <slot />
  </div>
</template>

<script>
import { getTextColor, getArrayRandomValue } from '@/helpers'

export default {
  name: 'AppCard',

  data() {
    return {
      backgroundImageTransition: {
        interval: 5 * 1000,
        index: 0,
        timer: undefined,
      },
    }
  },

  watch: {
    uniqueBackgroundImages: {
      immediate: true,
      deep: true,
      handler(uniqueBackgroundImages) {
        if (uniqueBackgroundImages.length > 1) this.startBackgroundTimer()
        else {
          this.backgroundImageTransition.index = 0
          this.stopBackgroundTimer()
        }
      },
    },
  },

  props: {
    primary: {
      type: Boolean,
      default: false,
    },

    secondary: {
      type: Boolean,
      default: false,
    },

    tertiary: {
      type: Boolean,
      default: false,
    },

    muted: {
      type: Boolean,
      default: false,
    },

    compact: {
      type: Boolean,
      default: false,
    },

    transparent: {
      type: Boolean,
      default: false,
    },

    noVerticalPadding: {
      type: Boolean,
      default: false,
    },

    inverted: {
      type: Boolean,
      default: false,
    },

    backgroundImage: {
      type: [String, Array],
    },

    borderRadius: {
      type: String,
    },

    borderTopRadius: {
      type: String,
    },

    borderBottomRadius: {
      type: String,
    },

    fullHeight: {
      type: Boolean,
      default: false,
    },

    topShadow: {
      type: Boolean,
      default: false,
    },

    minimalVerticalPadding: {
      type: Boolean,
      default: false,
    },

    // height: {
    //   type: String,
    // },
  },

  computed: {
    currentBackgroundImage() {
      return this.uniqueBackgroundImages[this.backgroundImageTransition.index]
    },

    uniqueBackgroundImages() {
      let backgroundImages = this.backgroundImage

      if (typeof backgroundImages === 'string')
        backgroundImages = [backgroundImages]

      return [...new Set(backgroundImages)]
    },

    backgroundColor() {
      const { $attrs } = this

      let backgroundColor =
        $attrs.backgroundColor ??
        $attrs.bgColor ??
        $attrs['background-color'] ??
        $attrs['bg-color']

      if (Array.isArray(backgroundColor)) {
        backgroundColor = getArrayRandomValue(backgroundColor)
      } else if (!backgroundColor) {
        const { primary, secondary, tertiary, muted } = this

        if (primary === true) backgroundColor = 'var(--primary-color)'
        else if (secondary === true) backgroundColor = 'var(--secondary-color)'
        else if (tertiary === true) backgroundColor = 'var(--tertiary-color)'
        else if (muted === true) backgroundColor = 'var(--muted-color)'
      }

      return backgroundColor
    },

    textColor() {
      let textColor = this.$attrs.textColor ?? this.$attrs['text-color']

      if (!textColor) {
        let { backgroundColor } = this

        if (backgroundColor) {
          if (backgroundColor.startsWith('var('))
            backgroundColor = getComputedStyle(document.body).getPropertyValue(
              backgroundColor.slice(4, -1),
            )

          textColor = getTextColor(backgroundColor)
        }
      }

      return textColor
    },

    noPadding() {
      return (
        this.$attrs['no-padding'] !== undefined ||
        this.$attrs.noPadding !== undefined
      )
    },

    noBorderRadius() {
      return (
        ['0', 0, '0px'].includes(this.borderRadius) ||
        this.$attrs['no-border-radius'] !== undefined ||
        this.$attrs.noBorderRadius !== undefined
      )
    },
  },

  methods: {
    nextBackgroundImage() {
      const backgroundImages = this.uniqueBackgroundImages
      const totalBackgroundImages = backgroundImages.length

      let nextIndex = this.backgroundImageTransition.index + 1

      if (nextIndex >= totalBackgroundImages) nextIndex = 0

      this.backgroundImageTransition.index = nextIndex
    },

    startBackgroundTimer() {
      this.stopBackgroundTimer()

      this.backgroundImageTransition.timer = setInterval(
        this.nextBackgroundImage,
        this.backgroundImageTransition.interval,
      )
    },

    stopBackgroundTimer() {
      const timer = this.backgroundImageTransition.timer

      if (timer) {
        clearInterval(timer)
        this.backgroundImageTransition.timer = undefined
      }
    },
  },
  //   if (this.height === 'stretch') {
  //     const parentHeight = Math.floor(this.$parent.$el.offsetHeight)

  //     this.$el.style.minHeight = `${parentHeight}px`
  //   }
  // },
}
</script>

<style lang="scss" scoped>
@use 'sass:math';

@use '@/styles/settings.scss' as *;
@use '@/styles/functions.scss' as *;

$padding: 1.5rem;
$borderRadius: 2rem;

:deep(.app-cards) {
  $padding: 1.5rem;

  &.horizontal-flow {
    > .app-card {
      padding: $padding;
      border-radius: $borderRadius;
    }
  }

  &.compact {
    $padding: math.div($padding, 2);

    > .app-card {
      padding: $padding $padding #{$padding * 2};
    }

    &.horizontal-flow {
      > .app-card {
        padding: $padding;
      }
    }
  }
}

.app-card {
  $defaultBackgroundColor: #fff;

  width: 100%;
  border-radius: $borderRadius $borderRadius 0 0;
  padding: $padding $padding #{$padding * 2};
  background-color: $defaultBackgroundColor;
  transition: background-image 1s ease-in-out;

  & + & {
    margin-top: -#{$padding};
    z-index: 1;

    &:first-child {
      margin-top: 0;
    }
  }

  // &:nth-child(even) {
  //   background-color: color.adjust($defaultBackgroundColor, $lightness: -5%);
  // }

  &:last-of-type {
    // margin-bottom: -#{$defaultPadding};
    border-radius: $borderRadius;
    padding-bottom: $padding;
  }

  &.rounded-b-none {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  &.top-shadow {
    box-shadow: 0px -3px 10px rgba(0, 0, 0, 0.1);
  }

  &.minimal-vertical-padding {
    $verticalPadding: math.div($padding, 2);
    padding-top: $verticalPadding;
    padding-bottom: $verticalPadding;
  }

  &.compact {
    $padding: math.div($padding, 2);
    padding: $padding $padding #{$padding * 2};
  }

  &.has-background-image {
    background-position: center;
    background-size: cover;
    background-color: transparent;
    background-repeat: no-repeat;
    text-shadow: 0 0 3px #000;
    color: #fff;
  }

  &.full-height {
    flex: 1;
    display: flex;
  }

  &.transparent {
    background-color: transparent;
  }

  // &.no-padding {
  //   overflow: hidden;
  // }

  &.no-vertical-padding {
    padding-top: 0;
    padding-bottom: 0;
  }

  &.no-border-radius {
    border-radius: 0;
  }

  // > .app-subheader {
  //   margin-bottom: $padding;
  // }

  // &.no-padding {
  //   :deep(.app-list) {
  //     $margin: math.div($padding, 2);

  //     &:first-child {
  //       margin-top: $margin;
  //     }

  //     &:last-child {
  //       margin-bottom: $margin;
  //     }
  //   }
  // }

  // &:not(.no-padding) {
  //   :deep(.app-list) {
  //     margin-top: -#{math.div($padding, 2)};
  //     margin-bottom: -#{math.div($padding, 2)};
  //     margin-left: -#{$padding};
  //     margin-right: -#{$padding};
  //   }
  // }

  :deep(> .negative-margin) {
    &:first-child {
      margin-top: -#{$padding};
    }

    &:last-child {
      margin-bottom: -#{$padding};
    }
  }

  &:not(.no-padding) {
    > .app-list {
      &:first-child {
        padding-top: 0;
        padding-bottom: 0;
      }
    }
  }
}
</style>
